import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import type FunnelWidgetProps from './FunnelWidget.types';

const FunnelWidget = ({
  data,
  description,
  widthFactor
}: FunnelWidgetProps) => {
  const theme = useTheme();
  const spacingHeight = theme.spacing(2);
  const adjustedHeight = `${parseFloat(spacingHeight) + 0.15}`;
  const adjustedHeightPx =
    parseFloat(getComputedStyle(document.documentElement).fontSize) *
    (parseFloat(spacingHeight) + 0.15);
  const containerRefs = useRef<HTMLDivElement[]>([]);
  const [widths, setWidths] = useState<number[]>([]);

  useEffect(() => {
    const collectedWidths = containerRefs.current.map(
      (ref) => ref?.offsetWidth || 0
    );
    setWidths(collectedWidths);
  }, []);

  return (
    <Stack
      data-testid="funnel-widget"
      direction="row"
      spacing={5}
      sx={{ width: '100%' }}
    >
      <Stack spacing={2} sx={{ flex: 1 }}>
        {data.map((stage, i) => {
          const currentWidth = 100 - i * widthFactor;

          return (
            <Box
              key={stage.label}
              ref={(el) => {
                if (el) {
                  containerRefs.current[i] = el as HTMLDivElement;
                }
              }}
              sx={{
                flex: 1,
                width: `${currentWidth}%`,
                alignSelf: 'center',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  backgroundColor: stage.color,
                  color: 'white',
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    alignSelf: 'flex-start'
                  }}
                >
                  {stage.label}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignSelf: 'flex-end',
                    justifyContent: 'flex-end',
                    alignItems: 'baseline',
                    textAlign: 'right',
                    flexWrap: 'wrap'
                  }}
                >
                  {stage.values.map((item) => (
                    <Box
                      key={`${item.title}-${item.value}`}
                      sx={{ display: 'flex', alignItems: 'baseline' }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 400, mr: 0.5, opacity: 0.7 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, fontHeight: 600 }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {i < data.length - 1 && widths.length > i + 1 && (
                <Box
                  sx={{
                    width: '100%',
                    height: adjustedHeight,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: `-${adjustedHeight}`
                  }}
                >
                  <svg
                    width="100%"
                    height={adjustedHeightPx}
                    viewBox={`0 0 ${widths[i]} ${adjustedHeightPx}`}
                    preserveAspectRatio="none"
                  >
                    <polygon
                      points={`0,0 ${widths[i]},0 ${(widths[i] + widths[i + 1]) / 2},${adjustedHeightPx} ${(widths[i] - widths[i + 1]) / 2},${adjustedHeightPx}`}
                      fill="#E0E0E0"
                    />
                  </svg>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      <Stack spacing={2} sx={{ flex: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'top' }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '12px',
              lineHeight: 1.4,
              fontWeight: 300,
              fontStyle: 'italic'
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

export default FunnelWidget;
